import React, { useCallback, useEffect, useRef, useState } from 'react'
import { collection, getDocs, orderBy, query as fsQuery } from 'firebase/firestore'
import { db } from '../../firebase/firebase'
import { BIBLE_BOOKS, OT_BOOKS, NT_BOOKS, UA_REF_MAP } from './bibleBooks'

const BIBLE_API = 'https://bible-api.com'
const BOLLS_API = 'https://bolls.life'

// ── Fetch helpers ─────────────────────────────────────────────────────────────

async function fetchVerses(b, ch, langCode) {
  if (langCode === 'ua') {
    const res = await fetch(`${BOLLS_API}/get-chapter/UBIO/${b.bookNum}/${ch}/`)
    if (!res.ok) throw new Error()
    return (await res.json()).map(v => ({ verse: v.verse, text: v.text }))
  } else {
    const res = await fetch(`${BIBLE_API}/${b.apiName}+${ch}?translation=web`)
    if (!res.ok) throw new Error()
    return (await res.json()).verses || []
  }
}

// ── Archive matching ──────────────────────────────────────────────────────────

function chaptersInRestInclude(rest, targetChapter) {
  const groups = rest.split(',')
  for (const group of groups) {
    const m = group.trim().match(/^(\d+)/)
    if (m && Number(m[1]) === targetChapter) return true
  }
  return false
}

function singleRefMatchesChapter(ref, targetBookId, targetChapter) {
  if (!ref) return false
  const norm = ref.toLowerCase().replace(/\s+/g, '')

  for (const [abbr, bookId] of Object.entries(UA_REF_MAP)) {
    if (bookId !== targetBookId) continue
    if (!norm.startsWith(abbr)) continue
    const rest = norm.slice(abbr.length).replace(/^[.\s]+/, '')
    return chaptersInRestInclude(rest, targetChapter)
  }

  for (const book of BIBLE_BOOKS) {
    if (book.id !== targetBookId) continue
    for (const nameNorm of [book.ua.toLowerCase().replace(/\s+/g, ''), book.en.toLowerCase().replace(/\s+/g, '')]) {
      if (norm.startsWith(nameNorm)) {
        const rest = norm.slice(nameNorm.length).replace(/^[.\s]+/, '')
        return chaptersInRestInclude(rest, targetChapter)
      }
    }
  }
  return false
}

function archiveMatchesChapter(bibleField, targetBookId, targetChapter) {
  if (!bibleField || !targetBookId || !targetChapter) return false
  return bibleField.split(';').some(ref => singleRefMatchesChapter(ref.trim(), targetBookId, targetChapter))
}

// ── Reference parser ──────────────────────────────────────────────────────────

function parseReference(raw) {
  const s = raw.trim().replace(/\s*:\s*/g, ':')

  const candidates = [
    s.match(/^(.+?)\s+(\d+)(?::(\d+)(?:-(\d+))?)?\.?$/i),
    s.match(/^(.+?)\s+(\d+)\s+(\d+)$/i),
  ]

  function findBook(raw) {
    const br = raw.replace(/\.$/, '').toLowerCase().trim()
    for (const [abbr, id] of Object.entries(UA_REF_MAP)) {
      if (br === abbr) return BIBLE_BOOKS.find(b => b.id === id)
    }
    return BIBLE_BOOKS.find(b =>
      b.en.toLowerCase().startsWith(br) ||
      b.ua.toLowerCase().startsWith(br) ||
      br === b.id
    ) || null
  }

  for (const m of candidates) {
    if (!m) continue
    const book = findBook(m[1])
    if (!book) continue
    const chapter = m[2]
    const verseStart = m[3] || null
    const verseEnd = m[4] || null
    let ref = `${book.apiName}+${chapter}`
    if (verseStart) ref += `:${verseStart}`
    if (verseEnd) ref += `-${verseEnd}`
    return { book, chapter: Number(chapter), verseStart, ref }
  }
  return null
}

// ── Bookmark helpers ──────────────────────────────────────────────────────────

function loadBookmarks() {
  try { return JSON.parse(localStorage.getItem('bibleBookmarks') || '[]') } catch { return [] }
}

function saveBookmarks(list) {
  localStorage.setItem('bibleBookmarks', JSON.stringify(list))
}

function formatRelativeDate(iso, lang) {
  const diff = Date.now() - new Date(iso).getTime()
  const days = Math.floor(diff / 86400000)
  if (days === 0) return lang === 'en' ? 'today' : 'сьогодні'
  if (days === 1) return lang === 'en' ? 'yesterday' : 'вчора'
  if (days < 7) return lang === 'en' ? `${days}d ago` : `${days} дн. тому`
  return new Date(iso).toLocaleDateString(lang === 'ua' ? 'uk-UA' : 'en-GB', { day: 'numeric', month: 'short' })
}

// ── Sub-components ────────────────────────────────────────────────────────────

const BookGrid = ({ books, label, lang, onBook }) => (
  <div style={s.testamentSection}>
    <div style={s.testamentLabel}>{label}</div>
    <div style={s.bookGrid}>
      {books.map(b => (
        <button key={b.id} style={s.bookBtn} onClick={() => onBook(b)}>
          {lang === 'en' ? b.en : b.ua}
        </button>
      ))}
    </div>
  </div>
)

const ChapterNav = ({ book, chapter, lang, onChapter, onBack }) => (
  <div style={s.chapterNav}>
    <button style={s.backBtn} onClick={onBack}>← {lang === 'en' ? 'Books' : 'Книги'}</button>
    <span style={s.breadcrumb}>{lang === 'en' ? book.en : book.ua}</span>
    <div style={s.chapterPills}>
      {Array.from({ length: book.chapters }, (_, i) => i + 1).map(n => (
        <button
          key={n}
          style={{ ...s.chapterPill, ...(n === chapter ? s.chapterPillActive : {}) }}
          onClick={() => onChapter(n)}
        >
          {n}
        </button>
      ))}
    </div>
  </div>
)

// ── Main component ────────────────────────────────────────────────────────────

const BibleViewer = () => {
  const [lang] = useState(localStorage.getItem('userLanguage') === 'en' ? 'en' : 'ua')

  const [view, setView] = useState('toc')
  const [book, setBook] = useState(null)
  const [chapter, setChapter] = useState(1)
  const [verses, setVerses] = useState([])
  const [versesAlt, setVersesAlt] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResult, setSearchResult] = useState(null)
  const [highlight, setHighlight] = useState('')

  // Reader settings
  const [parallel, setParallel] = useState(() => localStorage.getItem('bibleParallel') === '1')
  const [fontSize, setFontSize] = useState(() => Number(localStorage.getItem('bibleFontSize')) || 16)
  const [copied, setCopied] = useState(null)

  // Bookmarks
  const [bookmarks, setBookmarks] = useState(loadBookmarks)

  // Archive
  const [archiveItems, setArchiveItems] = useState([])
  const [relatedSermons, setRelatedSermons] = useState([])

  const parallelRef = useRef(parallel)
  useEffect(() => { parallelRef.current = parallel }, [parallel])

  // Load archive once
  useEffect(() => {
    getDocs(fsQuery(collection(db, 'archive'), orderBy('year', 'desc')))
      .then(snap => setArchiveItems(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {})
  }, [])

  // Filter related sermons
  useEffect(() => {
    if (!book || !chapter) { setRelatedSermons([]); return }
    setRelatedSermons(archiveItems.filter(item => archiveMatchesChapter(item.bible, book.id, chapter)))
  }, [book, chapter, archiveItems])

  // Font size
  const changeFont = (delta) => {
    setFontSize(prev => {
      const next = Math.min(24, Math.max(12, prev + delta))
      localStorage.setItem('bibleFontSize', String(next))
      return next
    })
  }

  // Fetch chapter
  const fetchChapter = useCallback(async (b, ch) => {
    setLoading(true)
    setError(null)
    setVerses([])
    setVersesAlt([])
    try {
      const primary = await fetchVerses(b, ch, lang)
      setVerses(primary)

      if (parallelRef.current) {
        const altLang = lang === 'ua' ? 'en' : 'ua'
        const alt = await fetchVerses(b, ch, altLang)
        setVersesAlt(alt)
      }

      setBook(b)
      setChapter(ch)
      setView('chapter')

      // Save last reading position
      localStorage.setItem('bibleLastPos', JSON.stringify({ bookId: b.id, chapter: ch }))
    } catch {
      setError(lang === 'en' ? 'Failed to load. Try again.' : 'Помилка завантаження. Спробуйте ще.')
    } finally {
      setLoading(false)
    }
  }, [lang])

  // Auto-restore last reading position on mount
  useEffect(() => {
    try {
      const lastPos = JSON.parse(localStorage.getItem('bibleLastPos') || 'null')
      if (!lastPos) return
      const b = BIBLE_BOOKS.find(bk => bk.id === lastPos.bookId)
      if (b) fetchChapter(b, lastPos.chapter)
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-fetch when parallel toggled while in chapter view
  const bookRef = useRef(book)
  const chapterRef = useRef(chapter)
  const viewRef = useRef(view)
  useEffect(() => { bookRef.current = book }, [book])
  useEffect(() => { chapterRef.current = chapter }, [chapter])
  useEffect(() => { viewRef.current = view }, [view])

  useEffect(() => {
    if (viewRef.current === 'chapter' && bookRef.current) {
      fetchChapter(bookRef.current, chapterRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parallel])

  // Copy verse
  const copyVerse = (v) => {
    const bookName = lang === 'ua' ? book.ua : book.en
    navigator.clipboard.writeText(`${bookName} ${chapter}:${v.verse} — ${v.text}`)
      .then(() => {
        setCopied(v.verse)
        setTimeout(() => setCopied(null), 1500)
      })
  }

  // Bookmarks
  const isBookmarked = (bookId, ch, verse) =>
    bookmarks.some(b => b.bookId === bookId && b.chapter === ch && b.verse === verse)

  const toggleBookmark = (v) => {
    const idx = bookmarks.findIndex(b => b.bookId === book.id && b.chapter === chapter && b.verse === v.verse)
    let next
    if (idx >= 0) {
      next = [...bookmarks.slice(0, idx), ...bookmarks.slice(idx + 1)]
    } else {
      const entry = {
        bookId: book.id,
        bookUa: book.ua,
        bookEn: book.en,
        chapter,
        verse: v.verse,
        text: v.text,
        date: new Date().toISOString(),
      }
      next = [entry, ...bookmarks].slice(0, 100)
    }
    setBookmarks(next)
    saveBookmarks(next)
  }

  const removeBookmark = (bookId, ch, verse) => {
    const next = bookmarks.filter(b => !(b.bookId === bookId && b.chapter === ch && b.verse === verse))
    setBookmarks(next)
    saveBookmarks(next)
  }

  const openBookmark = (bm) => {
    const b = BIBLE_BOOKS.find(bk => bk.id === bm.bookId)
    if (!b) return
    setHighlight(String(bm.verse))
    fetchChapter(b, bm.chapter).then(() => {
      setTimeout(() => {
        const el = document.getElementById(`verse-${bm.verse}`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 400)
    })
  }

  // Search
  const handleSearch = useCallback(async (e) => {
    e.preventDefault()
    const q = searchQuery.trim()
    if (!q) return

    const parsed = parseReference(q)
    if (parsed) {
      setHighlight(parsed.verseStart || '')
      await fetchChapter(parsed.book, parsed.chapter)
      if (parsed.verseStart) {
        setTimeout(() => {
          const el = document.getElementById(`verse-${parsed.verseStart}`)
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 400)
      }
      return
    }

    setLoading(true)
    setError(null)
    setSearchResult(null)
    try {
      let resultVerses
      if (lang === 'ua') {
        const res = await fetch(`${BOLLS_API}/search/UBIO/?search=${encodeURIComponent(q)}`)
        if (!res.ok) throw new Error()
        const data = await res.json()
        resultVerses = data.map(v => ({
          book_name: BIBLE_BOOKS.find(bk => bk.bookNum === v.book)?.[lang] || '',
          chapter: v.chapter,
          verse: v.verse,
          text: v.text.replace(/<\/?mark>/g, ''),
        }))
      } else {
        const res = await fetch(`${BIBLE_API}/${encodeURIComponent(q)}?translation=web`)
        if (!res.ok) throw new Error()
        const data = await res.json()
        resultVerses = data.verses || []
      }
      if (resultVerses.length) {
        setSearchResult({ q, verses: resultVerses })
        setView('search')
      } else {
        setError(lang === 'en' ? 'No results found.' : 'Нічого не знайдено.')
      }
    } catch {
      setError(lang === 'en'
        ? 'Enter a verse reference (e.g. "John 3:16") or book name.'
        : 'Введіть посилання (напр. "Ів. 3:16") або назву книги.')
    } finally {
      setLoading(false)
    }
  }, [searchQuery, lang, fetchChapter])

  const goToc = () => {
    setView('toc'); setBook(null); setVerses([]); setVersesAlt([])
    setSearchResult(null); setError(null); setHighlight('')
  }

  // ── Verse row ─────────────────────────────────────────────────────────────

  const renderVerse = (v) => {
    const vAlt = versesAlt.find(va => va.verse === v.verse)
    const isHighlit = String(v.verse) === highlight
    const bookmarked = isBookmarked(book.id, chapter, v.verse)
    return (
      <div key={v.verse} id={`verse-${v.verse}`} className='verse-row' style={{ ...s.verseRow, ...(isHighlit ? s.verseHighlight : {}) }}>
        <span style={s.verseNum}>{v.verse}</span>
        {parallel ? (
          <div style={s.parallelCols}>
            <span style={{ ...s.verseText, fontSize }}>{v.text}</span>
            <span style={{ ...s.verseText, fontSize, ...s.verseTextAlt }}>{vAlt?.text || '…'}</span>
          </div>
        ) : (
          <span style={{ ...s.verseText, fontSize }}>{v.text}</span>
        )}
        <div className='verse-actions' style={s.verseActions}>
          <button
            className='verse-action-btn'
            style={{ ...s.verseAction, ...(copied === v.verse ? s.copyBtnDone : {}) }}
            onClick={() => copyVerse(v)}
            title={lang === 'en' ? 'Copy verse' : 'Копіювати вірш'}
          >
            {copied === v.verse ? '✓' : '⎘'}
          </button>
          <button
            className='verse-action-btn'
            style={{ ...s.verseAction, ...(bookmarked ? s.bmActive : {}) }}
            onClick={() => toggleBookmark(v)}
            title={bookmarked
              ? (lang === 'en' ? 'Remove bookmark' : 'Видалити закладку')
              : (lang === 'en' ? 'Add bookmark' : 'Додати закладку')}
          >
            {bookmarked ? '★' : '☆'}
          </button>
        </div>
      </div>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const primaryLabel = lang === 'ua' ? '🇺🇦 Огієнко' : '🇬🇧 World English'
  const altLabel     = lang === 'ua' ? '🇬🇧 World English' : '🇺🇦 Огієнко'

  return (
    <div style={s.wrap}>
      <style>{`
        @media (max-width: 767px) {
          .bible-padded { padding-left: 12px !important; padding-right: 12px !important; }
          .verse-row { flex-wrap: wrap !important; }
          .verse-actions {
            width: 100% !important;
            padding-left: 28px !important;
            display: flex !important;
            gap: 8px !important;
            margin-top: 2px !important;
          }
        }
      `}</style>

      {/* Toolbar */}
      <div style={s.toolbar} className='bible-padded'>
        <form onSubmit={handleSearch} style={s.searchForm}>
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={lang === 'en'
              ? 'Search — e.g. "John 3:16" or "love"'
              : 'Пошук — напр. "Ів. 3:16" або "любов"'}
            style={s.searchInput}
          />
          <button type='submit' style={s.searchBtn} disabled={loading}>
            {loading ? '…' : '🔍'}
          </button>
        </form>

        <div style={s.controls}>
          <button
            style={{ ...s.controlBtn, ...(parallel ? s.controlBtnActive : {}) }}
            onClick={() => setParallel(p => { const next = !p; localStorage.setItem('bibleParallel', next ? '1' : '0'); return next })}
            title={lang === 'en' ? 'Parallel translation' : 'Паралельний переклад'}
          >
            ∥
          </button>

          <div style={s.fontRow}>
            <button style={s.fontBtn} onClick={() => changeFont(-2)}>A−</button>
            <span style={s.fontVal}>{fontSize}</span>
            <button style={s.fontBtn} onClick={() => changeFont(2)}>A+</button>
          </div>

          {/* Bookmarks button */}
          <button
            style={{ ...s.controlBtn, ...(view === 'bookmarks' ? s.controlBtnActive : {}), position: 'relative' }}
            onClick={() => view === 'bookmarks' ? goToc() : setView('bookmarks')}
            title={lang === 'en' ? 'Bookmarks' : 'Закладки'}
          >
            ★
            {bookmarks.length > 0 && (
              <span style={s.bmBadge}>{bookmarks.length}</span>
            )}
          </button>
        </div>
      </div>

      {/* Parallel column headers */}
      {parallel && view === 'chapter' && (
        <div style={s.parallelHeader} className='bible-padded'>
          <span style={s.parallelLang}>{primaryLabel}</span>
          <span style={s.parallelLang}>{altLabel}</span>
        </div>
      )}

      {error && <div style={s.errorBox} className='bible-padded'>{error}</div>}

      {/* TOC */}
      {view === 'toc' && (
        <div style={s.toc} className='bible-padded'>
          <BookGrid books={OT_BOOKS} label={lang === 'en' ? '📜 Old Testament' : '📜 Старий Заповіт'} lang={lang} onBook={b => fetchChapter(b, 1)} />
          <BookGrid books={NT_BOOKS} label={lang === 'en' ? '✝ New Testament' : '✝ Новий Заповіт'} lang={lang} onBook={b => fetchChapter(b, 1)} />
        </div>
      )}

      {/* Chapter */}
      {view === 'chapter' && book && (
        <div>
          <div className='bible-padded'>
            <ChapterNav
              book={book}
              chapter={chapter}
              lang={lang}
              onChapter={n => { setHighlight(''); fetchChapter(book, n) }}
              onBack={goToc}
            />
          </div>
          {loading
            ? <div style={s.loading}>{lang === 'en' ? 'Loading…' : 'Завантаження…'}</div>
            : <div style={s.verseList}>{verses.map(v => renderVerse(v))}</div>
          }

          {!loading && verses.length > 0 && (
            <div style={s.chapterFooter} className='bible-padded'>
              {chapter > 1 && (
                <button style={s.navFooterBtn} onClick={() => { setHighlight(''); fetchChapter(book, chapter - 1) }}>
                  ← {lang === 'en' ? 'Previous' : 'Попередня'}
                </button>
              )}
              {chapter < book.chapters && (
                <button style={{ ...s.navFooterBtn, marginLeft: 'auto' }} onClick={() => { setHighlight(''); fetchChapter(book, chapter + 1) }}>
                  {lang === 'en' ? 'Next' : 'Наступна'} →
                </button>
              )}
            </div>
          )}

          {/* Related sermons */}
          {relatedSermons.length > 0 && (
            <div style={s.sermonsPanel} className='bible-padded'>
              <div style={s.sermonsTitle}>
                📺 {lang === 'en' ? 'Sermons on this chapter' : 'Проповіді на цю главу'} ({relatedSermons.length})
              </div>
              <div style={s.sermonsList}>
                {relatedSermons.map(item => {
                  const vid = (item.link || '').match(/(?:v=|youtu\.be\/)([A-Za-z0-9_-]{11})/)
                  return (
                    <div key={item.id} style={s.sermonCard}>
                      {vid && <img src={`https://img.youtube.com/vi/${vid[1]}/mqdefault.jpg`} alt='' style={s.sermonThumb} />}
                      <div style={s.sermonInfo}>
                        <div style={s.sermonCardTitle}>{item.title}</div>
                        <div style={s.sermonMeta}>{item.author} · {item.year}</div>
                        {item.bible && <div style={s.sermonBible}>{item.bible}</div>}
                      </div>
                      {vid && (
                        <a href={`https://www.youtube.com/watch?v=${vid[1]}`} target='_blank' rel='noopener noreferrer' style={s.sermonLink}>▶</a>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bookmarks view */}
      {view === 'bookmarks' && (
        <div className='bible-padded'>
          <div style={s.searchHeader}>
            <button style={s.backBtn} onClick={goToc}>← {lang === 'en' ? 'Books' : 'Книги'}</button>
            <span style={s.breadcrumb}>
              {lang === 'en' ? 'Bookmarks' : 'Закладки'}
              {bookmarks.length > 0 && <span style={s.bmCount}> ({bookmarks.length})</span>}
            </span>
          </div>
          {bookmarks.length === 0 ? (
            <div style={s.bmEmpty}>
              <div style={s.bmEmptyIcon}>☆</div>
              <div>{lang === 'en'
                ? 'No bookmarks yet. Tap ☆ on any verse to save it.'
                : 'Закладок ще немає. Натисніть ☆ на будь-якому вірші, щоб зберегти.'}</div>
            </div>
          ) : (
            <div style={s.bmList}>
              {bookmarks.map((bm, i) => {
                const bookName = lang === 'ua' ? bm.bookUa : bm.bookEn
                return (
                  <div key={i} style={s.bmCard} onClick={() => openBookmark(bm)}>
                    <div style={s.bmCardHeader}>
                      <span style={s.bmRef}>★ {bookName} {bm.chapter}:{bm.verse}</span>
                      <span style={s.bmDate}>{formatRelativeDate(bm.date, lang)}</span>
                      <button
                        style={s.bmRemove}
                        onClick={e => { e.stopPropagation(); removeBookmark(bm.bookId, bm.chapter, bm.verse) }}
                        title={lang === 'en' ? 'Remove' : 'Видалити'}
                      >
                        ×
                      </button>
                    </div>
                    <div style={s.bmText}>
                      {bm.text?.length > 140 ? bm.text.slice(0, 140) + '…' : bm.text}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Search results */}
      {view === 'search' && searchResult && (
        <div className='bible-padded'>
          <div style={s.searchHeader}>
            <button style={s.backBtn} onClick={goToc}>← {lang === 'en' ? 'Books' : 'Книги'}</button>
            <span style={s.breadcrumb}>
              {lang === 'en' ? `Results for "${searchResult.q}"` : `Результати: "${searchResult.q}"`}
            </span>
          </div>
          <div style={s.verseList}>
            {searchResult.verses.map((v, i) => (
              <div key={i} style={s.verseRow}>
                <span
                  style={{ ...s.verseNum, ...s.refLink }}
                  onClick={() => {
                    const b = BIBLE_BOOKS.find(bk => bk.en === v.book_name || bk.ua === v.book_name)
                    if (!b) return
                    setHighlight(String(v.verse))
                    fetchChapter(b, v.chapter).then(() => {
                      setTimeout(() => {
                        const el = document.getElementById(`verse-${v.verse}`)
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                      }, 400)
                    })
                  }}
                >
                  {v.book_name} {v.chapter}:{v.verse}
                </span>
                <span style={{ ...s.verseText, fontSize }}>{v.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && view !== 'chapter' && (
        <div style={s.loading}>{lang === 'en' ? 'Loading…' : 'Завантаження…'}</div>
      )}
    </div>
  )
}

export default BibleViewer

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  wrap: { fontFamily: '"Segoe UI", system-ui, sans-serif', color: '#2d1f14' },

  toolbar: { display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' },
  searchForm: { display: 'flex', flex: 1, minWidth: '200px' },
  searchInput: { flex: 1, padding: '10px 14px', border: '1.5px solid #e8d8c8', borderRight: 'none', borderRadius: '8px 0 0 8px', fontSize: '14px', outline: 'none', color: '#2d1f14' },
  searchBtn: { padding: '10px 16px', background: '#8b6044', color: '#fff', border: 'none', borderRadius: '0 8px 8px 0', cursor: 'pointer', fontSize: '16px' },

  controls: { display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 },
  controlBtn: { padding: '8px 14px', border: '1.5px solid #e8d8c8', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '18px', fontWeight: '700', color: '#9a7c63', fontFamily: 'inherit', lineHeight: 1 },
  controlBtnActive: { background: '#8b6044', borderColor: '#8b6044', color: '#fff' },
  fontRow: { display: 'flex', alignItems: 'center', border: '1.5px solid #e8d8c8', borderRadius: '8px', overflow: 'hidden' },
  fontBtn: { padding: '8px 10px', border: 'none', background: '#fdfaf7', cursor: 'pointer', fontSize: '12px', fontWeight: '700', color: '#8b6044', fontFamily: 'inherit' },
  fontVal: { padding: '0 8px', fontSize: '12px', color: '#9a7c63', fontWeight: '600', minWidth: '24px', textAlign: 'center' },

  bmBadge: { position: 'absolute', top: '-6px', right: '-6px', background: '#c4986a', color: '#fff', fontSize: '10px', fontWeight: '700', borderRadius: '10px', padding: '1px 5px', lineHeight: '14px' },

  parallelHeader: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px', marginBottom: '8px', paddingLeft: '40px', paddingRight: '56px' },
  parallelLang: { fontSize: '11px', fontWeight: '700', color: '#9a7c63', textTransform: 'uppercase', letterSpacing: '0.5px' },

  errorBox: { background: '#fdf4ec', border: '1px solid #e8d8c8', borderRadius: '8px', padding: '12px 16px', fontSize: '14px', color: '#8b6044', marginBottom: '20px' },

  toc: { display: 'flex', flexDirection: 'column', gap: '28px' },
  testamentSection: {},
  testamentLabel: { fontSize: '13px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#9a7c63', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #f0e4d4' },
  bookGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '8px' },
  bookBtn: { padding: '9px 10px', background: '#fdfaf7', border: '1.5px solid #f0e4d4', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#2d1f14', textAlign: 'left', fontFamily: 'inherit' },

  chapterNav: { marginBottom: '20px' },
  backBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#8b6044', fontSize: '14px', fontWeight: '600', padding: '0', marginBottom: '10px', fontFamily: 'inherit' },
  breadcrumb: { display: 'block', fontSize: '20px', fontWeight: '700', color: '#2d1f14', marginBottom: '14px' },
  chapterPills: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  chapterPill: { padding: '5px 10px', borderRadius: '6px', border: '1.5px solid #e8d8c8', background: '#fdfaf7', cursor: 'pointer', fontSize: '13px', color: '#2d1f14', fontFamily: 'inherit', minWidth: '36px', textAlign: 'center' },
  chapterPillActive: { background: '#8b6044', borderColor: '#8b6044', color: '#fff', fontWeight: '700' },

  verseList: { display: 'flex', flexDirection: 'column', gap: '2px' },
  verseRow: { display: 'flex', gap: '6px', padding: '6px 8px', borderRadius: '6px', alignItems: 'flex-start' },
  verseHighlight: { background: '#fdf4ec', border: '1px solid #c4986a' },
  verseNum: { flexShrink: 0, fontSize: '11px', fontWeight: '700', color: '#c4986a', minWidth: '24px', marginTop: '3px', textAlign: 'right' },
  verseText: { lineHeight: '1.65', color: '#2d1f14', flex: 1 },

  parallelCols: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px', flex: 1 },
  verseTextAlt: { color: '#5a4035', borderLeft: '2px solid #f0e4d4', paddingLeft: '16px' },

  verseActions: { display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 },
  verseAction: { flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#c4986a', padding: '2px 3px', opacity: 0.4, lineHeight: 1, marginTop: '2px', fontFamily: 'inherit' },
  copyBtnDone: { opacity: 1, color: '#3aaa6e' },
  bmActive: { opacity: 1, color: '#c4986a' },

  refLink: { cursor: 'pointer', minWidth: '100px', fontSize: '12px', textDecoration: 'underline', color: '#8b6044' },

  chapterFooter: { display: 'flex', gap: '12px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #f0e4d4' },
  navFooterBtn: { padding: '10px 20px', background: 'transparent', border: '1.5px solid #c4986a', borderRadius: '8px', cursor: 'pointer', color: '#8b6044', fontSize: '14px', fontWeight: '600', fontFamily: 'inherit' },

  searchHeader: { marginBottom: '20px' },

  sermonsPanel: { marginTop: '32px', paddingTop: '24px', borderTop: '2px solid #f0e4d4' },
  sermonsTitle: { fontSize: '14px', fontWeight: '700', color: '#8b6044', marginBottom: '16px' },
  sermonsList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  sermonCard: { display: 'flex', gap: '12px', alignItems: 'center', background: '#fdfaf7', border: '1px solid #f0e4d4', borderRadius: '10px', padding: '10px', overflow: 'hidden' },
  sermonThumb: { width: '80px', height: '56px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 },
  sermonInfo: { flex: 1, minWidth: 0 },
  sermonCardTitle: { fontSize: '14px', fontWeight: '600', color: '#2d1f14', marginBottom: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  sermonMeta: { fontSize: '12px', color: '#9a7c63' },
  sermonBible: { fontSize: '12px', color: '#c4986a', marginTop: '2px', fontStyle: 'italic' },
  sermonLink: { flexShrink: 0, background: '#8b6044', color: '#fff', borderRadius: '8px', padding: '10px 14px', fontSize: '14px', textDecoration: 'none', lineHeight: 1 },

  /* Bookmarks */
  bmCount: { fontSize: '16px', fontWeight: '400', color: '#9a7c63' },
  bmEmpty: { textAlign: 'center', padding: '60px 20px', color: '#9a7c63', fontSize: '15px', lineHeight: '1.6' },
  bmEmptyIcon: { fontSize: '48px', marginBottom: '16px', opacity: 0.4 },
  bmList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  bmCard: { background: '#fdfaf7', border: '1.5px solid #f0e4d4', borderRadius: '10px', padding: '14px 16px', cursor: 'pointer' },
  bmCardHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' },
  bmRef: { fontWeight: '700', fontSize: '14px', color: '#8b6044', flex: 1 },
  bmDate: { fontSize: '11px', color: '#b8a090', flexShrink: 0 },
  bmRemove: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#c4986a', padding: '0 2px', lineHeight: 1, fontFamily: 'inherit', opacity: 0.6 },
  bmText: { fontSize: '14px', color: '#5a4035', lineHeight: '1.5' },

  loading: { textAlign: 'center', padding: '40px', color: '#9a7c63', fontSize: '16px' },
}
